import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useDateFilterVersion } from "@/lib/useDateFilterVersion";
import { Shell, Head, TotalRow, useEsc, useRowNav, navRowCls, navRowProps, fmtV, fmtQ, fmtD } from "./ReportShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function StockTransfersView({ onClose }) {
  const [data, setData] = useState(null);
  const dfv = useDateFilterVersion();
  useEsc(onClose);
  const [sel, setSel] = useRowNav(data ? data.rows.length : 0);
  useEffect(() => {
    axios.get(`${API}/inventory/stock-transfers`).then((r) => setData(r.data)).catch(() => {});
  }, [dfv]);
  if (!data) return null;
  return (
    <Shell title="Stock Transfers / Movement Register" onBack={onClose} testid="stock-transfers-view"
      subtitle={<><span>All stock movements (auto from bills)</span><span>{data.period}</span></>}>
      <Head cols={[{ t: "Date", w: "w-[100px]" }, { t: "Stock Item" }, { t: "Type", w: "w-[130px]", center: true },
        { t: "Voucher / Ref", w: "w-[220px]" }, { t: "Inward Qty", w: "w-[120px]", right: true },
        { t: "Outward Qty", w: "w-[120px]", right: true }, { t: "Balance", w: "w-[120px]", right: true }]} />
      <div className="flex-1 overflow-y-auto" data-testid="st-rows">
        {data.rows.map((r, i) => (
          <div key={i} {...navRowProps(i, sel, setSel)} className={`flex items-center text-sm text-slate-900 ${navRowCls(sel === i)}`} data-testid={`st-row-${i}`}>
            <div className="w-[100px] px-2 py-0.5">{fmtD(r.date)}</div>
            <div className="flex-1 px-2 py-0.5 font-semibold">{r.item}</div>
            <div className="w-[130px] px-2 py-0.5 text-center">{r.type}</div>
            <div className="w-[220px] px-2 py-0.5 text-xs">{r.ref}</div>
            <div className="w-[120px] px-2 py-0.5 text-right">{r.in_q ? `${fmtQ(r.in_q)} ${r.unit}` : ""}</div>
            <div className="w-[120px] px-2 py-0.5 text-right">{r.out_q ? `${fmtQ(r.out_q)} ${r.unit}` : ""}</div>
            <div className="w-[120px] px-2 py-0.5 text-right font-semibold">{r.balance == null ? "" : fmtQ(r.balance) || "0"}</div>
          </div>
        ))}
        {data.rows.length === 0 && <div className="px-4 py-6 text-center text-xs text-slate-500">No stock movements yet — they appear automatically as bills are created</div>}
      </div>
      <TotalRow testid="st-totals" cells={[{ t: "Totals" }, { t: fmtQ(data.totals.in_q), w: "w-[120px]", right: true },
        { t: fmtQ(data.totals.out_q), w: "w-[120px]", right: true }, { t: "", w: "w-[120px]" }]} />
    </Shell>
  );
}

export function PhysicalStockView({ onClose }) {
  const [data, setData] = useState(null);
  const dfv = useDateFilterVersion();
  useEsc(onClose);
  const [sel, setSel] = useRowNav(data ? data.rows.length : 0);
  useEffect(() => {
    axios.get(`${API}/inventory/physical-stock`).then((r) => setData(r.data)).catch(() => {});
  }, [dfv]);
  if (!data) return null;
  return (
    <Shell title="Physical Stock Register" onBack={onClose} testid="physical-stock-view"
      subtitle={<><span>Book stock as per software records</span><span>As on {fmtD(data.as_on)}</span></>}>
      <Head cols={[{ t: "Stock Item" }, { t: "Stock Group", w: "w-[160px]" }, { t: "Unit", w: "w-[80px]", center: true },
        { t: "Book Quantity", w: "w-[140px]", right: true }, { t: "Rate", w: "w-[120px]", right: true },
        { t: "Value", w: "w-[150px]", right: true }]} />
      <div className="flex-1 overflow-y-auto" data-testid="ps-rows">
        {data.rows.map((r, i) => (
          <div key={i} {...navRowProps(i, sel, setSel)} className={`flex items-center text-sm text-slate-900 ${navRowCls(sel === i)}`} data-testid={`ps-row-${i}`}>
            <div className="flex-1 px-2 py-0.5 font-semibold">{r.name}</div>
            <div className="w-[160px] px-2 py-0.5 text-xs">{r.group}</div>
            <div className="w-[80px] px-2 py-0.5 text-center">{r.unit}</div>
            <div className="w-[140px] px-2 py-0.5 text-right">{fmtQ(r.qty) || "0"}</div>
            <div className="w-[120px] px-2 py-0.5 text-right italic">{fmtV(r.rate)}</div>
            <div className="w-[150px] px-2 py-0.5 text-right">{fmtV(r.value)}</div>
          </div>
        ))}
      </div>
      <TotalRow testid="ps-totals" cells={[{ t: "Grand Total" }, { t: fmtQ(data.grand_total.qty), w: "w-[140px]", right: true },
        { t: "", w: "w-[120px]" }, { t: fmtV(data.grand_total.value), w: "w-[150px]", right: true }]} />
    </Shell>
  );
}

export function MovementAnalysisView({ onClose }) {
  const [data, setData] = useState(null);
  const dfv = useDateFilterVersion();
  useEsc(onClose);
  const [sel, setSel] = useRowNav(data ? data.rows.length : 0);
  useEffect(() => {
    axios.get(`${API}/inventory/movement-analysis`).then((r) => setData(r.data)).catch(() => {});
  }, [dfv]);
  if (!data) return null;
  return (
    <Shell title="Movement Analysis" onBack={onClose} testid="movement-analysis-view"
      subtitle={<><span>Item-wise inward / outward with average rates</span><span>{data.period}</span></>}>
      <Head cols={[{ t: "Stock Item" }, { t: "Inwards Qty", w: "w-[110px]", right: true }, { t: "Avg Rate", w: "w-[95px]", right: true },
        { t: "Inwards Value", w: "w-[125px]", right: true }, { t: "Outwards Qty", w: "w-[110px]", right: true },
        { t: "Avg Rate", w: "w-[95px]", right: true }, { t: "Outwards Value", w: "w-[125px]", right: true },
        { t: "Net Qty", w: "w-[95px]", right: true }]} />
      <div className="flex-1 overflow-y-auto" data-testid="ma-rows">
        {data.rows.map((r, i) => (
          <div key={i} {...navRowProps(i, sel, setSel)} className={`flex items-center text-sm text-slate-900 ${navRowCls(sel === i)}`} data-testid={`ma-row-${i}`}>
            <div className="flex-1 px-2 py-0.5 font-semibold">{r.name}</div>
            <div className="w-[110px] px-2 py-0.5 text-right">{fmtQ(r.in_q)}</div>
            <div className="w-[95px] px-2 py-0.5 text-right italic">{fmtV(r.in_rate)}</div>
            <div className="w-[125px] px-2 py-0.5 text-right">{fmtV(r.in_v)}</div>
            <div className="w-[110px] px-2 py-0.5 text-right">{fmtQ(r.out_q)}</div>
            <div className="w-[95px] px-2 py-0.5 text-right italic">{fmtV(r.out_rate)}</div>
            <div className="w-[125px] px-2 py-0.5 text-right">{fmtV(r.out_v)}</div>
            <div className={`w-[95px] px-2 py-0.5 text-right font-semibold ${r.net_q < 0 ? "text-red-700" : ""}`}>{fmtQ(r.net_q) || "0"}</div>
          </div>
        ))}
        {data.rows.length === 0 && <div className="px-4 py-6 text-center text-xs text-slate-500">No movements in this period</div>}
      </div>
      <TotalRow testid="ma-totals" cells={[{ t: "Totals" }, { t: fmtV(data.totals.in_v), w: "w-[125px]", right: true },
        { t: "", w: "w-[110px]" }, { t: "", w: "w-[95px]" }, { t: fmtV(data.totals.out_v), w: "w-[125px]", right: true }, { t: "", w: "w-[95px]" }]} />
    </Shell>
  );
}

export function AgeingView({ onClose }) {
  const [data, setData] = useState(null);
  const dfv = useDateFilterVersion();
  useEsc(onClose);
  const [sel, setSel] = useRowNav(data ? data.rows.length : 0);
  useEffect(() => {
    axios.get(`${API}/inventory/ageing`).then((r) => setData(r.data)).catch(() => {});
  }, [dfv]);
  if (!data) return null;
  const B = ({ b }) => <span>{b.q ? `${fmtQ(b.q)} · ${fmtV(b.v)}` : ""}</span>;
  return (
    <Shell title="Stock Ageing Analysis" onBack={onClose} testid="ageing-view"
      subtitle={<><span>Closing stock aged by purchase date (FIFO)</span><span>As on {fmtD(data.as_on)}</span></>}>
      <Head cols={[{ t: "Stock Item" }, { t: "Total Qty · Value", w: "w-[170px]", right: true },
        { t: "0–30 days", w: "w-[150px]", right: true }, { t: "31–60 days", w: "w-[150px]", right: true },
        { t: "61–90 days", w: "w-[150px]", right: true }, { t: "> 90 days", w: "w-[150px]", right: true }]} />
      <div className="flex-1 overflow-y-auto" data-testid="ag-rows">
        {data.rows.map((r, i) => (
          <div key={i} {...navRowProps(i, sel, setSel)} className={`flex items-center text-sm text-slate-900 ${navRowCls(sel === i)}`} data-testid={`ag-row-${i}`}>
            <div className="flex-1 px-2 py-0.5 font-semibold">{r.name}</div>
            <div className="w-[170px] px-2 py-0.5 text-right font-semibold">{fmtQ(r.qty)} · {fmtV(r.value)}</div>
            <div className="w-[150px] px-2 py-0.5 text-right"><B b={r.b0} /></div>
            <div className="w-[150px] px-2 py-0.5 text-right"><B b={r.b30} /></div>
            <div className="w-[150px] px-2 py-0.5 text-right"><B b={r.b60} /></div>
            <div className="w-[150px] px-2 py-0.5 text-right"><B b={r.b90} /></div>
          </div>
        ))}
        {data.rows.length === 0 && <div className="px-4 py-6 text-center text-xs text-slate-500">No closing stock to age</div>}
      </div>
      <TotalRow testid="ag-totals" cells={[{ t: "Grand Total" },
        { t: `${fmtQ(data.grand_total.qty)} · ${fmtV(data.grand_total.value)}`, w: "w-[320px]", right: true }]} />
    </Shell>
  );
}
